import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import {
  EXTERNAL_PARTNERS_AND_ADVISERS_NAMES,
  WORK_EXPERIENCE_NAMES as EXTERNAL_PARTNERS_WORK_EXPERIENCE_NAMES,
  LIST_OF_EXTERNAL_PARTNERS_NAMES,
} from 'components/BusinessToolForm/Sections/ManagementAndOrganization/ExternalPartners/utils/form';
import {
  INSTITUTION_NAMES,
  MANAGEMENT_TEAM_NAMES,
  PERSONNEL_PROFILES_NAMES,
  WORK_EXPERIENCE_NAMES,
} from 'components/BusinessToolForm/Sections/ManagementAndOrganization/ManagementTeam/utils/form';
import {
  LEVEL2_DEPARTMENT_NAMES,
  LEVEL3_DEPARTMENT_NAMES,
  STAFF_ORGANIZATION_NAMES,
} from 'components/BusinessToolForm/Sections/ManagementAndOrganization/StaffOrganization/utils/form';

import { ul } from '../helpers';

const LIST_OF_INSTITUTIONS_INSTRUCTIONS = {
  [INSTITUTION_NAMES.NAME]: {
    name: 'Name der Bildungseinrichtung',
    instruction: 'Geben Sie die vollständigen Namen der besuchten Bildungseinrichtungen an.',
    exampleOne: 'Amerikanische Universität',
    exampleTwo: 'Oklahoma State University',
  },
  [INSTITUTION_NAMES.SPECIALIZATION]: {
    name: 'Fachrichtung',
    instruction:
      'Geben Sie das Fachgebiet oder die Studienrichtung an, die die Schlüsselpersonen während ihrer Ausbildung verfolgt haben.',
    exampleOne: 'Maschinenbau',
    exampleTwo: 'Finanzen',
  },
};

const WORK_EXPERIENCE_INSTRUCTIONS = {
  [WORK_EXPERIENCE_NAMES.EMPLOYER]: {
    name: 'Name des Arbeitgebers',
    instruction: 'Nennen Sie deutlich den vollständigen Namen des Arbeitgebers oder der Organisation.',
    exampleOne: 'John Deere',
    exampleTwo: 'ARP Dend',
  },
  [WORK_EXPERIENCE_NAMES.POSITION]: {
    name: 'Position',
    instruction:
      'Geben Sie die Positionsbezeichnung oder den Jobtitel der Schlüsselperson im Unternehmen an und heben Sie Rolle und Verantwortlichkeiten prägnant und korrekt hervor.',
    exampleOne: 'Chief Engineer der Abteilung für landwirtschaftliche Entwicklung',
    exampleTwo: 'Finanzmanager',
  },
  [WORK_EXPERIENCE_NAMES.FROM]: {
    name: 'Zeitraum',
    instruction:
      'Geben Sie eine detaillierte Chronologie des beruflichen Werdegangs an, indem Sie die jeweiligen Zeiträume anführen.',
    exampleOne: '2000',
    exampleTwo: '2017',
  },
  [WORK_EXPERIENCE_NAMES.TO]: {
    name: 'Zeitraum',
    instruction:
      'Geben Sie eine detaillierte Chronologie des beruflichen Werdegangs an, indem Sie die jeweiligen Zeiträume anführen.',
    exampleOne: '2013',
    exampleTwo: '2023',
  },
  [WORK_EXPERIENCE_NAMES.FUNCTIONS]: {
    name: 'Aufgaben',
    instruction:
      'Beschreiben Sie die Verantwortlichkeiten und Arbeitsbereiche, in die diese Person eingebunden war. Betonen Sie die Bedeutung der Beiträge der Person für die Erreichung der Unternehmensziele.',
    exampleOne:
      'Ausarbeitung und Umsetzung der Technologie­strategie des Unternehmens in Einklang mit den übergeordneten Geschäfts­zielen. Leitung der Forschungs- und Entwicklungsaktivitäten zur Innovation und Verbesserung des Produktportfolios des Unternehmens.',
    exampleTwo:
      'Entwicklung und Pflege von Finanzmodellen zur Prognose der finanziellen Performance des Unternehmens. Analyse von Finanzdaten, um Einblicke in Umsatztrends, Kostenstrukturen und Profitabilität zu liefern. Bewertung der finanziellen Risiken des Unternehmens und Entwicklung von Strategien zu deren Minderung.',
  },
};

const PERSONNEL_PROFILES_INSTRUCTIONS = {
  [PERSONNEL_PROFILES_NAMES.FILE]: {
    name: '',
    instruction:
      'Laden Sie ein hochauflösendes Bild hoch und stellen Sie sicher, dass es professionellen Standards entspricht und mit dem Unternehmens­auftritt übereinstimmt.',
    exampleOne: '',
    exampleTwo: '',
  },
  [PERSONNEL_PROFILES_NAMES.NAME]: {
    name: 'Vorname',
    instruction: 'Geben Sie den Vornamen des Schlüsselmitarbeiters an.',
    exampleOne: 'Oliver',
    exampleTwo: 'Mary',
  },
  [PERSONNEL_PROFILES_NAMES.SURNAME]: {
    name: 'Nachname',
    instruction: 'Geben Sie den Nachnamen des Schlüsselmitarbeiters an.',
    exampleOne: 'Smith',
    exampleTwo: 'Wilson',
  },
  [PERSONNEL_PROFILES_NAMES.DATE_OF_BIRTH]: {
    name: 'Geburtsdatum',
    instruction:
      'Geben Sie das Geburtsdatum in einem klaren und eindeutigen Format an, einschließlich Tag, Monat und Jahr.',
    exampleOne: '01.01.1975',
    exampleTwo: '02.05.1983',
  },
  [PERSONNEL_PROFILES_NAMES.POSITION]: {
    name: 'Aktuelle Position',
    instruction:
      'Geben Sie den Jobtitel oder die Position der Schlüsselperson im Unternehmen an und heben Sie Rolle und Verantwortlichkeiten prägnant und korrekt hervor.',
    exampleOne: 'Technologiechef',
    exampleTwo: 'Finanzmanager',
  },
  [PERSONNEL_PROFILES_NAMES.LIST_OF_INSTITUTIONS]: LIST_OF_INSTITUTIONS_INSTRUCTIONS,
  [PERSONNEL_PROFILES_NAMES.WORK_EXPERIENCE]: WORK_EXPERIENCE_INSTRUCTIONS,
  [PERSONNEL_PROFILES_NAMES.KEY_SKILLS]: {
    name: 'Kernkompetenzen',
    instruction:
      'Listen Sie die wichtigsten Fähigkeiten auf, die für eine erfolgreiche Tätigkeit als Führungskraft erforderlich sind. Heben Sie Kompetenzen hervor, die die Eignung für die jeweilige Rolle belegen.',
    exampleOne: ul([
      'Maschinenbau',
      'Konstruktion von Landmaschinen',
      'Projektmanagement',
      'Führungskompetenz',
      'Technische Problemlösung',
    ]),
    exampleTwo: ul([
      'Finanzplanung und -analyse',
      'Budgetierung',
      'Forecasting',
      'Finanzberichterstattung',
      'Problemlösung',
    ]),
  },
  [PERSONNEL_PROFILES_NAMES.ACHIEVEMENTS]: {
    name: 'Besondere Erfolge',
    instruction:
      'Geben Sie einen Überblick über die wichtigsten Erfolge in diesem Bereich. Definieren Sie klar die spezifische Expertise oder Spezialisierung, die diese Person auszeichnet. Beschreiben Sie bemerkenswerte Leistungen, Qualifikationen oder Meilensteine, die ein hohes Kompetenzniveau in ihrem Fachgebiet belegen.',
    exampleOne:
      'Bei John Deere spielte er eine Schlüsselrolle bei der Entwicklung von Motorsystemen für Jan85-Eskalatoren. Er nimmt jährlich an internationalen Maschinenausstellungen teil, darunter die International EXPO und Innovation in Chicago. Fünf Jahre in Folge belegte er den Spitzenplatz in Connecticuts Ranking „Promising Engineers“.',
    exampleTwo:
      'Zertifizierte Chartered Financial Analyst (CFA). Mary wurde für ihre herausragenden Beiträge im Finanzbereich ausgezeichnet und erhielt renommierte Preise wie den Financial Excellence Award 2020 und den Finance Professional of the Year Award 2021.',
  },
};

const MANAGEMENT_TEAM_INSTRUCTIONS = {
  [MANAGEMENT_TEAM_NAMES.HISTORY]: {
    name: 'Geschichte und Hintergrund',
    instruction:
      'Beschreiben Sie die Entstehungsgeschichte Ihres Managementteams. Erläutern Sie den Prozess der Zusammenstellung des Teams, die wichtigsten Phasen, Rollen, Hintergründe und Beiträge der einzelnen Mitglieder. Heben Sie die Expertise und Erfahrung hervor, die jedes Mitglied einbringt.',
    exampleOne:
      'Das Unternehmen wurde von den US-Amerikanern John Johnson und Oliver Smith gegründet. John Johnson, Inhaber eines kleinen landwirtschaftlichen Betriebs in Texas, tat sich mit Oliver Smith zusammen, einem Ingenieur bei John Deere, einem renommierten Hersteller von landwirtschaftlichen, Bau- und Forstmaschinen.',
    exampleTwo:
      'Zu den wichtigsten Projektbeteiligten im Unternehmen zählen John Johnson und Mary Wilson. Das Unternehmen wurde von John Johnson gegründet, einem Absolventen der A University mit Schwerpunkt Programmierung. Gemeinsam mit Mary Wilson, einer Finanzabsolventin derselben Universität, baute er das Unternehmen auf.',
  },
  [MANAGEMENT_TEAM_NAMES.SKILLS]: {
    name: 'Fähigkeiten und Kompetenzen',
    instruction:
      'Beschreiben Sie die wichtigsten Fähigkeiten und Kompetenzen des Managementteams. Heben Sie die Führungs- und Fachqualitäten hervor, die das Team besonders geeignet machen, das Unternehmen zu leiten. Konzentrieren Sie sich auf Stärken, die zum Erfolg des Unternehmens beitragen.',
    exampleOne:
      'Die umfassende Branchenerfahrung und die Management- und Administrationskompetenzen des Führungsteams qualifizieren es als leistungsstarke Leitungsebene. John Johnsons Unternehmergeist und Branchenkenntnisse, kombiniert mit Oliver Smiths technischer Expertise, bilden die Grundlage für strategische Ausrichtung und Produktinnovation.',
    exampleTwo:
      'John Johnsons Programmierkenntnisse bilden das Fundament der Plattform. Er entwickelte zudem die Architektur der ersten Version. Mary Wilsons Erfahrung in den Finanzabteilungen verschiedener Unternehmen hat ihre Fähigkeiten im Bereich Finanzmanagement wesentlich gestärkt.',
  },
  [MANAGEMENT_TEAM_NAMES.PERSONAL_QUALITIES]: {
    name: 'Persönliche Eigenschaften',
    instruction:
      'Beschreiben Sie die persönlichen Eigenschaften der Teammitglieder mit Fokus auf Soft Skills, Anpassungsfähigkeit, Resilienz und Zusammenarbeit. Heben Sie die Eigenschaften hervor, die entscheidend für die Erreichung der Unternehmensziele und die Sicherung der Wettbewerbsfähigkeit sind.',
    exampleOne:
      'Das Managementteam vereint Führungsstärke und professionelle Qualitäten. Die Führung des Unternehmens während der COVID-19-Pandemie hat die Bedeutung der Anpassungsfähigkeit an externe Faktoren deutlich gemacht. Die ausgeprägten Soft Skills des Teams erleichterten das Mitarbeitendenmanagement in Krisenzeiten und ermöglichten es, Lösungen für neue Herausforderungen zu entwickeln.',
    exampleTwo:
      'Resilienz, Anpassungsfähigkeit, Teamfähigkeit und Innovationsgeist sind zentrale persönliche Eigenschaften im Team des Unternehmens. Diese Eigenschaften fördern ein dynamisches Arbeitsumfeld, in dem Herausforderungen gemeistert, Veränderungen angenommen und Prozesse und Lösungen kontinuierlich verbessert werden.',
  },
  [MANAGEMENT_TEAM_NAMES.PERSONNEL_PROFILES]: PERSONNEL_PROFILES_INSTRUCTIONS,
};

const EXTERNAL_PARTNERS_WORK_EXPERIENCE_INSTRUCTIONS = {
  [EXTERNAL_PARTNERS_WORK_EXPERIENCE_NAMES.EMPLOYER]: {
    name: 'Name des Arbeitgebers',
    instruction: 'Nennen Sie deutlich den vollständigen Namen des Arbeitgebers oder der Organisation.',
    exampleOne: 'Lint',
    exampleTwo: 'A&Partners',
  },
  [EXTERNAL_PARTNERS_WORK_EXPERIENCE_NAMES.POSITION]: {
    name: 'Position',
    instruction:
      'Geben Sie die Positionsbezeichnung oder den Jobtitel des Schlüsselpartners im Unternehmen an und heben Sie Rolle und Verantwortlichkeiten prägnant und korrekt hervor.',
    exampleOne: 'Softwareentwickler',
    exampleTwo: 'Vertragsanwalt',
  },
};

const LIST_OF_EXTERNAL_PARTNERS_INSTRUCTIONS = {
  [LIST_OF_EXTERNAL_PARTNERS_NAMES.FILE]: {
    name: '',
    instruction:
      'Laden Sie ein hochauflösendes Bild hoch und stellen Sie sicher, dass es professionellen Standards entspricht und mit dem Unternehmens­auftritt übereinstimmt.',
    exampleOne: '',
    exampleTwo: '',
  },
  [LIST_OF_EXTERNAL_PARTNERS_NAMES.NAME]: {
    name: 'Vorname',
    instruction: 'Geben Sie den Vornamen des Schlüsselpartners an.',
    exampleOne: 'Alex',
    exampleTwo: 'Camila',
  },
  [LIST_OF_EXTERNAL_PARTNERS_NAMES.SURNAME]: {
    name: 'Nachname',
    instruction: 'Geben Sie den Nachnamen des Schlüsselpartners an.',
    exampleOne: 'Leto',
    exampleTwo: 'Devis',
  },
  [LIST_OF_EXTERNAL_PARTNERS_NAMES.ROLE]: {
    name: 'Rolle im Unternehmen',
    instruction:
      'Geben Sie die Positionsbezeichnung oder Rolle des Schlüsselpartners im Unternehmen an und heben Sie Aufgaben und Verantwortlichkeiten prägnant und korrekt hervor.',
    exampleOne: 'IT-Berater',
    exampleTwo: 'Rechtsberaterin',
  },
  [LIST_OF_EXTERNAL_PARTNERS_NAMES.WORK_EXPERIENCE]: EXTERNAL_PARTNERS_WORK_EXPERIENCE_INSTRUCTIONS,
  [LIST_OF_EXTERNAL_PARTNERS_NAMES.KEY_SKILLS]: {
    name: 'Kernkompetenzen',
    instruction:
      'Listen Sie die wichtigsten Fähigkeiten Ihres Beraters auf. Heben Sie Kompetenzen hervor, die die Eignung für die zugewiesene Rolle belegen.',
    exampleOne: 'IT-Strategie und Planung \nCybersicherheit \nSoftwareentwicklung',
    exampleTwo: 'Rechtskonformität \nSchutz geistigen Eigentums \nJuristische Schulung und Weiterbildung',
  },
};

const EXTERNAL_PARTNERS_AND_ADVISERS_INSTRUCTIONS = {
  [EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.LIST_OF_EXTERNAL_PARTNERS]: LIST_OF_EXTERNAL_PARTNERS_INSTRUCTIONS,
  [EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.PARTNERS]: {
    name: 'Expertise der Partner',
    instruction:
      'Listen Sie alle Partner und Berater auf, mit denen das Unternehmen zusammenarbeitet. Geben Sie die Expertise an, die jeder Partner einbringt, und erläutern Sie seine Rolle bei der Unterstützung der Unternehmensziele. Stellen Sie klar dar, wie die Kompetenzen jedes Partners dem Unternehmen zugutekommen.',
    exampleOne:
      'Das Unternehmen arbeitet mit der IT-Beratung ABCD zusammen, die Expertise in Technologie­infrastruktur, Cybersicherheit und Softwareentwicklung bietet. ABCD unterstützt das Unternehmen bei der Implementierung und Wartung robuster IT-Systeme und -Lösungen.',
    exampleTwo:
      'Das Unternehmen arbeitet mit der Rechtsberaterin Camila Davis zusammen, um die Einhaltung regulatorischer Anforderungen sicherzustellen und rechtliche Risiken zu minimieren. Die Marketingagentur ABC bringt Expertise in digitalem Marketing, Branding und Werbung ein und hilft dem Unternehmen, seine Zielgruppe zu erreichen.',
  },
  [EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.CRITERIA]: {
    name: 'Auswahlkriterien',
    instruction:
      'Definieren Sie Auswahlkriterien wie erfolgreiche Projekte, Kundenreferenzen, Passung, Branchenreputation oder Übereinstimmung mit Unternehmenszielen. Betonen Sie, dass externe Berater über die spezifischen Fähigkeiten und Kenntnisse verfügen müssen, die erforderlich sind, um einen bedeutenden Beitrag zum Projekt zu leisten.',
    exampleOne:
      'Das wichtigste Kriterium bei der Auswahl von Partnern ist deren Fachkompetenz und Erfahrung. Partner sollten über spezialisierte Expertise und umfangreiche Erfahrungen in relevanten Bereichen verfügen.',
    exampleTwo:
      'Bei der Partnerauswahl berücksichtigt das Unternehmen deren Erfahrung, Reputation und Kosten­effizienz. Partner sollten wettbewerbsfähige Preise und kosteneffiziente Lösungen anbieten, die mit dem Budget des Unternehmens übereinstimmen. Partner müssen zuverlässig und vertrauenswürdig sein, in der Lage, Fristen einzuhalten, Zusagen zu erfüllen und Vertraulichkeit zu wahren.',
  },
  [EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.PLANS]: {
    name: 'Expansionspläne',
    instruction:
      'Beschreiben Sie die Absichten, das Netzwerk der Partner und Berater zu erweitern, und begründen Sie dies – etwa durch Zugang zu neuen Märkten, Erweiterung der Expertise oder Diversifizierung von Perspektiven. Heben Sie potenzielle Vorteile und die Übereinstimmung mit den strategischen Zielen des Unternehmens hervor.',
    exampleOne:
      'Das Unternehmen beabsichtigt, sein Netzwerk an Partnern und Beratern zu erweitern, um von vielfältiger Expertise und unterschiedlichen Perspektiven zu profitieren. Diese Erweiterung soll die strategische Entscheidungsfindung stärken und die Gesamtleistung des Unternehmens verbessern.',
    exampleTwo:
      'Das Unternehmen plant, künftig einen Finanzberater aus einem Investmentfonds einzubinden, um Finanzprozesse zu optimieren und das Schuldenmanagement zu unterstützen.',
  },
};

const LEVEL2_DEPARTMENT_INSTRUCTIONS = {
  [LEVEL2_DEPARTMENT_NAMES.DEPARTMENT]: {
    name: 'Organisationsstruktur',
    instruction:
      'Beschreiben Sie die übergeordnete Organisationsstruktur und heben Sie hervor, wie verschiedene Abteilungen oder Einheiten zusammenarbeiten, um gemeinsame Ziele zu erreichen.',
    exampleOne: '',
    exampleTwo: '',
  },
};

const LEVEL3_DEPARTMENT_INSTRUCTIONS = {
  [LEVEL3_DEPARTMENT_NAMES.DEPARTMENT]: {
    name: 'Organisationsstruktur',
    instruction:
      'Beschreiben Sie die übergeordnete Organisationsstruktur und heben Sie hervor, wie verschiedene Abteilungen oder Einheiten zusammenarbeiten, um gemeinsame Ziele zu erreichen.',
    exampleOne: '',
    exampleTwo: '',
  },
};

const STAFF_ORGANIZATION_INSTRUCTIONS = {
  [STAFF_ORGANIZATION_NAMES.NUMBER_OF_STAFF]: {
    name: 'Mitarbeitendenzahl & Struktur',
    instruction:
      'Geben Sie einen Überblick über die aktuelle Anzahl der Mitarbeitenden im Unternehmen. Nennen Sie die Gesamtzahl und skizzieren Sie kurz die Zusammensetzung der Belegschaft, einschließlich wichtiger Abteilungen oder Teams.',
    exampleOne:
      'Das Unternehmen beschäftigt derzeit 50 Mitarbeitende, die auf fünf Abteilungen verteilt sind: Marketing und Vertrieb, Produktion, Personalwesen, Finanzen sowie Forschung und Entwicklung.',
    exampleTwo:
      'Das Unternehmen beschäftigt derzeit 12 Mitarbeitende in 3 Abteilungen. Die technische Abteilung ist für die Entwicklung und Wartung der Plattform zuständig. Die operative Abteilung ist für HR-Aktivitäten und Marketing verantwortlich. Die Finanzabteilung kümmert sich um das Finanzmanagement.',
  },
  [STAFF_ORGANIZATION_NAMES.LEVEL1_DEPARTMENT]: {
    name: 'Organisationsstruktur',
    instruction:
      'Beschreiben Sie die übergeordnete Organisationsstruktur und heben Sie hervor, wie verschiedene Abteilungen oder Einheiten zusammenarbeiten, um gemeinsame Ziele zu erreichen.',
    exampleOne: '',
    exampleTwo: '',
  },
  [STAFF_ORGANIZATION_NAMES.LEVEL2_DEPARTMENT]: LEVEL2_DEPARTMENT_INSTRUCTIONS,
  [STAFF_ORGANIZATION_NAMES.LEVEL3_DEPARTMENT]: LEVEL3_DEPARTMENT_INSTRUCTIONS,
  [STAFF_ORGANIZATION_NAMES.SKILLS]: {
    name: 'Fähigkeiten und Qualifikationen',
    instruction:
      'Listen Sie die Fähigkeiten und Qualifikationen auf, die Ihrer Ansicht nach erforderlich sind, damit Mitarbeitende ihre Aufgaben erfolgreich erfüllen können. Schließen Sie technische Fähigkeiten, Ausbildung und relevante Berufserfahrung ein. Formulieren Sie prägnant und konkret.',
    exampleOne:
      'Wesentlich ist, dass Mitarbeitende einen Abschluss in Maschinenbau oder eine vergleichbare Qualifikation besitzen, Erfahrung in der Konstruktion von Schwermaschinen, Problemlösungskompetenz, Kenntnisse landwirtschaftlicher Praktiken, ausgeprägte Kommunikationsfähigkeiten, Erfahrung in der Teamarbeit und Vertrautheit mit Umweltvorschriften.',
    exampleTwo:
      'Plattformentwickler sollten Experten in Programmiersprachen wie Python, JavaScript oder Java sein. Sie benötigen außerdem Erfahrung in der Softwareentwicklung, fundierte Kenntnisse in Datenbankmanagementsystemen und Vertrautheit mit Cloud-Plattformen wie AWS oder Azure.',
  },
  [STAFF_ORGANIZATION_NAMES.ADDITIONAL_HIRES]: {
    name: 'Personalbedarf',
    instruction:
      'Identifizieren Sie Art und Anzahl der Mitarbeitenden, die zur Umsetzung des Projekts benötigt werden. Skizzieren Sie Rollen, Fähigkeiten und Expertise, die die Projektentwicklung unterstützen sollen.',
    exampleOne:
      'Für das Projekt müssen 15 neue Mitarbeitende eingestellt werden. Vorgesehen ist die Rekrutierung von 5 Entwicklungsingenieuren mit Erfahrung in der Konstruktion von Landmaschinen. Zudem sollen 10 technische Fachkräfte für die Produktionsabteilung eingestellt werden, um die gestiegene Fertigungsnachfrage zu bewältigen.',
    exampleTwo:
      'Das geplante Personalwachstum umfasst die Schaffung der Position eines Project Coordinators, um die Projektentwicklung zu unterstützen. Diese Person wird Projektaktivitäten koordinieren und die Dokumentation verwalten. Der Project Coordinator sollte über ausgeprägte organisatorische und kommunikative Fähigkeiten verfügen und in der Lage sein, mehrere Aufgaben parallel zu managen.',
  },
  [STAFF_ORGANIZATION_NAMES.APPROACHES]: {
    name: 'Recruiting-Ansätze',
    instruction:
      'Beschreiben Sie die Recruiting-Strategien des Unternehmens. Stellen Sie die Personen vor, die für den Einstellungsprozess verantwortlich sind. Führen Sie die Methoden auf, mit denen Kandidat:innen gewonnen und ausgewählt werden, um einen gründlichen und effizienten Recruiting-Prozess sicherzustellen.',
    exampleOne:
      'Die Personalabteilung ist für den Einstellungsprozess verantwortlich. Das Unternehmen nutzt verschiedene Tools zur Optimierung des Recruitings, darunter Bewerber­managementsysteme zur Steuerung von Stellenausschreibungen, Kandidatenbewerbungen und Kommunikation während des gesamten Rekrutierungszyklus.',
    exampleTwo:
      'Der HR-Manager, Theodore Swift, überwacht den Recruiting- und Onboarding-Prozess. Er nutzt Online-Jobportale und Recruiting-Software, um Kandidat:innen effizient zu gewinnen und zu prüfen.',
  },
  [STAFF_ORGANIZATION_NAMES.ATTRACTING_DIFFICULTIES]: {
    name: 'Herausforderungen bei der Gewinnung',
    instruction:
      'Identifizieren Sie Herausforderungen bei der Gewinnung von Mitarbeitenden. Dazu können Skill-Mismatches, ein wettbewerbsintensiver Arbeitsmarkt oder ein Mangel an qualifizierten Fachkräften gehören. Beschreiben Sie kurz alle Hürden, die den Einstellungsprozess beeinträchtigen können.',
    exampleOne:
      'Herausforderungen im Recruiting umfassen Skill-Mismatches, begrenzte Talentpools und Konkurrenz mit anderen Branchen. Technische Expertise ist entscheidend, aber die Gewinnung qualifizierter Fachkräfte ist insbesondere in ländlichen Regionen schwierig.',
    exampleTwo:
      'Das Unternehmen steht vor Herausforderungen bei der Personalgewinnung, beispielsweise durch einen starken Wettbewerb um qualifizierte Fachkräfte. Hochqualifizierte Spezialisten fordern höhere Gehälter und umfangreichere Zusatzleistungen als andere Berufsgruppen.',
  },
  [STAFF_ORGANIZATION_NAMES.SALARY_POLICY]: {
    name: 'Vergütung und Motivation',
    instruction:
      'Beschreiben Sie Ihre Vergütungspolitik im Verhältnis zum Markt. Erläutern Sie bestehende Motivationsprogramme wie Boni, Gewinnbeteiligung oder Anerkennungsinitiativen. Führen Sie Leistungskennzahlen oder Ziele auf, an die Anreize geknüpft sind.',
    exampleOne:
      'Die Vergütungspolitik des Unternehmens sieht über dem Marktdurchschnitt liegende Gehälter vor, um Top-Talente zu gewinnen. Zusätzlich bestehen Gewinnbeteiligungsprogramme zur weiteren Motivation. Anerkennungsinitiativen und an Leistungskennzahlen gekoppelte Ziele steigern die Motivation zusätzlich.',
    exampleTwo:
      'Die Vergütungspolitik des Unternehmens orientiert sich an Marktstandards und bietet wettbewerbsfähige Vergütungspakete. Motivationsprogramme umfassen leistungsabhängige Boni. Anreize sind an individuelle und teambezogene Leistungskennzahlen gekoppelt, um die Ausrichtung an den Unternehmenszielen sicherzustellen.',
  },
  [STAFF_ORGANIZATION_NAMES.RETENTION_DIFFICULTIES]: {
    name: 'Bindungsherausforderungen',
    instruction:
      'Identifizieren Sie Herausforderungen bei der Mitarbeitendenbindung. Beschreiben Sie Strategien zur Erhöhung der Bindung, etwa wettbewerbsfähige Vergütung, Weiterbildungsangebote, eine inklusive Unternehmenskultur, Anerkennungsprogramme usw.',
    exampleOne:
      'Das Unternehmen steht vor Herausforderungen aufgrund hoher körperlicher Belastung und saisonaler Schwankungen im Arbeitsvolumen. Zur Verbesserung der Bindung bieten wir wettbewerbsfähige Vergütung, Weiterbildungs­möglichkeiten, eine inklusive Kultur, Anerkennungsprogramme und Work-Life-Balance-Maßnahmen.',
    exampleTwo:
      'Zu den Bindungsherausforderungen zählen Unzufriedenheit im Job und aggressive Abwerbepolitik von Wettbewerbern. Die Strategien des Unternehmens umfassen wettbewerbsfähige Vergütung, berufliche Entwicklungsmöglichkeiten und die Förderung einer inklusiven Unternehmenskultur.',
  },
  [STAFF_ORGANIZATION_NAMES.DEVELOPMENT_PROGRAMS]: {
    name: 'Schulungsprogramme',
    instruction:
      'Beschreiben Sie ausführlich die bestehenden Programme zur Mitarbeiterentwicklung und -schulung. Erläutern Sie die eingesetzten Trainingsmethoden für neue und bestehende Mitarbeitende. Betonen Sie die Bedeutung der Entwicklung von Kompetenzen und deren Beitrag zu den Unternehmenszielen.',
    exampleOne:
      'Das Unternehmen wird Schulungs- und Entwicklungspläne implementieren, um Mitarbeitende optimal auf ihre Aufgaben vorzubereiten. Neue Mitarbeitende durchlaufen ein umfassendes Onboarding. Alle Mitarbeitenden erhalten Zugang zu kontinuierlichen Lernangeboten, darunter Workshops, Seminare, Webinare und Online-Kurse.',
    exampleTwo:
      'Das Unternehmen verpflichtet sich zur Einführung einer umfassenden Weiterbildungsstrategie. Regelmäßige Workshops sollen Fähigkeiten in Projektmanagement, Kommunikation und Kundenservice stärken. Mitarbeitende erhalten Zugang zu externen Kursen und Zertifizierungen, um ihre Expertise weiter auszubauen.',
  },
};

export const MANAGEMENT_AND_ORGANIZATION_INSTRUCTIONS = {
  [BUSINESS_TOOL_SUB_SECTIONS.MANAGEMENT_AND_ORGANIZATION.MANAGEMENT_TEAM]: MANAGEMENT_TEAM_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.MANAGEMENT_AND_ORGANIZATION.EXTERNAL_PARTNERS_AND_ADVISERS]:
    EXTERNAL_PARTNERS_AND_ADVISERS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.MANAGEMENT_AND_ORGANIZATION.STAFF_ORGANIZATION]: STAFF_ORGANIZATION_INSTRUCTIONS,
};
